import React, { useContext, useEffect, useState } from 'react'
import { Space, Typography, Button, Input, Alert, Modal, Divider, UploadProps, Upload } from 'antd'

import styles from './Settings.module.css'
import './Settings.module.css'
import { FileImageOutlined, InboxOutlined, UserOutlined } from '@ant-design/icons'

import { InfoCxt } from '../../App'
import { useCookies } from 'react-cookie'
import { RcFile } from 'antd/es/upload'
import {
  AdminRole,
  decodeAccessToken,
  getOwnUuidByEmail,
  getProfile,
  updateProfilePhotoUrl,
  updateUsername,
} from '../../services/admin-auth-service'

const { Title, Text } = Typography
const { Dragger } = Upload

export const Profile = () => {
  const [cookies] = useCookies(['loginToken'])
  const { message, modal, setUsername: setGlobalUsername, setAvatar: setGlobalAvatar } = useContext(InfoCxt)

  const [ownId, setOwnId] = useState<string | null>(null)
  const [role, setRole] = useState<AdminRole | null>(null)
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [avatar, setAvatar] = useState('')
  const [loadError, setLoadError] = useState('')

  const [usernameInput, setUsernameInput] = useState('')
  const [error, setError] = useState('')
  const [isUsernameModalOpen, setIsUsernameModalOpen] = useState(false)
  const [usernameSaving, setUsernameSaving] = useState(false)

  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false)
  const [avatarBase64, setAvatarBase64] = useState('')
  const [avatarError, setAvatarError] = useState('')
  const [avatarSaving, setAvatarSaving] = useState(false)

  const canEditAvatar = role !== 'ROLE_ORGANIZATION'

  useEffect(() => {
    const loadProfile = async () => {
      const decoded = decodeAccessToken(cookies.loginToken ?? '')
      if (!decoded?.email || !decoded?.role) {
        setLoadError('Unable to identify the logged-in user.')
        return
      }
      const currentRole = decoded.role as AdminRole
      setRole(currentRole)
      try {
        const id = await getOwnUuidByEmail(decoded.email, currentRole)
        if (!id) {
          setLoadError('Could not find your account.')
          return
        }
        setOwnId(id)
        const profile = await getProfile(id, currentRole)
        setUsername(profile.username)
        setEmail(profile.email)
        setAvatar(profile.profilePhotoUrl ?? '')
      } catch (e: any) {
        setLoadError(e.message)
      }
    }
    loadProfile()
  }, [cookies.loginToken])

  const editUsername = () => {
    setUsernameInput(username)
    setError('')
    setIsUsernameModalOpen(true)
  }

  const onUsernameCancel = () => {
    setIsUsernameModalOpen(false)
  }

  const onUsernameOk = async () => {
    if (!ownId || !role) return
    if (!usernameInput.trim()) {
      setError('Username cannot be empty.')
      return
    }
    setError('')
    setUsernameSaving(true)
    try {
      await updateUsername(ownId, role, usernameInput.trim())
      setUsername(usernameInput.trim())
      setGlobalUsername?.(usernameInput.trim())
      setIsUsernameModalOpen(false)
      message?.success('Username successfully updated!')
    } catch (e: any) {
      setError(e.message)
    } finally {
      setUsernameSaving(false)
    }
  }

  const openAvatarModal = () => {
    setAvatarBase64('')
    setAvatarError('')
    setIsAvatarModalOpen(true)
  }

  const onAvatarCancel = () => {
    setIsAvatarModalOpen(false)
  }

  const onAvatarOk = async () => {
    if (!ownId || !role || avatarError || !avatarBase64) return
    setAvatarSaving(true)
    try {
      await updateProfilePhotoUrl(ownId, role, avatarBase64)
      setAvatar(avatarBase64)
      setGlobalAvatar?.(avatarBase64)
      setIsAvatarModalOpen(false)
      message?.success('Avatar successfully updated!')
    } catch (e: any) {
      setAvatarError(e.message)
    } finally {
      setAvatarSaving(false)
    }
  }

  const checkImageDimension = (file: RcFile): Promise<boolean> => {
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.addEventListener('load', (event) => {
        const _loadedImageUrl = event.target?.result
        const fileType = file.type
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg']
        if (!allowedTypes.includes(fileType)) {
          setAvatarError('Invalid file type. Please upload a valid image file.')
          resolve(false)
          return
        }
        const imageSize = file.size / 1024
        const limitKb = 500
        if (imageSize > limitKb) {
          setAvatarError(`Image size must be smaller than ${limitKb}KB!`)
          resolve(false)
          return
        }
        const image = document.createElement('img')
        image.src = _loadedImageUrl as string

        image.addEventListener('load', () => {
          const { width, height } = image
          if (width > 3000 || height > 3000) {
            setAvatarError('Image size too large, please upload an image smaller than 3000 x 3000 px.')
            resolve(false)
          } else if (width < 500 || height < 500) {
            setAvatarError('Image size too small, please upload an image larger than 500 x 500 px.')
            resolve(false)
          } else {
            setAvatarError('')
            setAvatarBase64(_loadedImageUrl as string)
            resolve(false)
          }
        })
      })
    })
  }

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    maxCount: 1,
    beforeUpload: checkImageDimension,
    accept: '.png, .jpg, .jpeg',
    showUploadList: {
      showDownloadIcon: true,
      downloadIcon: 'Download',
      showRemoveIcon: true,
    },
  }

  const openRemoveAvatarModal = () => {
    modal?.confirm({
      wrapClassName: 'settings-page-modal',
      maskClosable: true,
      title: 'Confirm Removal',
      content: 'Are you sure you want to remove the avatar?',
      okText: 'Confirm',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        if (!ownId || !role) return
        try {
          await updateProfilePhotoUrl(ownId, role, '')
          setAvatar('')
          setGlobalAvatar?.('')
          message?.success('Avatar successfully removed!')
        } catch (e: any) {
          message?.error(e.message)
        }
      },
    })
  }

  return (
    <div className={styles.settingsPageTabContent}>
      <div className={styles.settingsSectionHeading}>
        <Title level={4} style={{ marginBottom: 4 }}>Edit Profile</Title>
        <Text type='secondary'>View and manage your personal profile information.</Text>
      </div>

      {loadError && <Alert style={{ marginBottom: 16 }} type='error' showIcon message={loadError} />}

      <div className={styles.settingsRow}>
        <div className={styles.settingsRowLeft}>
          <span className={styles.settingsIconBadge}>
            <UserOutlined />
          </span>
          <div>
            <div><Text strong>Username</Text></div>
            <Text type='secondary'>Set a username for your profile.</Text>
          </div>
        </div>
        <div className={styles.settingsRowValue}>{username || '-'}</div>
        <div className={styles.settingsRowActions}>
          <Button onClick={editUsername} disabled={!ownId}>Edit</Button>
        </div>
      </div>
      <Divider style={{ margin: 0 }} />

      <div className={styles.settingsRow}>
        <div className={styles.settingsRowLeft}>
          <span className={styles.settingsIconBadge}>
            <FileImageOutlined />
          </span>
          <div>
            <div><Text strong>Avatar</Text></div>
            <Text type='secondary'>
              {canEditAvatar
                ? 'Select an avatar to personalise your account.'
                : 'Avatar updates are not available for organization accounts.'}
            </Text>
          </div>
        </div>
        <div className={styles.settingsRowValue}>
          {avatar ? (
            <img src={avatar} alt='User Avatar' style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover' }} />
          ) : (
            <Text type='secondary'>No Avatar</Text>
          )}
        </div>
        <div className={styles.settingsRowActions}>
          <Button onClick={openAvatarModal} disabled={!ownId || !canEditAvatar}>Change</Button>
          <Button onClick={openRemoveAvatarModal} disabled={!ownId || !canEditAvatar || !avatar}>Remove</Button>
        </div>
      </div>

      {/* use Modal component because we need to dynmaically render alert */}
      <Modal
        title='Edit Username'
        open={isUsernameModalOpen}
        footer={[
          <Button key='cancel' onClick={onUsernameCancel}>
            Cancel
          </Button>,
          <Button key='submit' type='primary' loading={usernameSaving} onClick={onUsernameOk}>
            Save
          </Button>,
        ]}
        maskClosable={true}
        closable={false}
        onCancel={onUsernameCancel}
        className={'settings-page-modal'}>
        <Space direction='vertical' style={{ width: '100%' }}>
          {error && <Alert message={error} type='error' showIcon />}
          <Text>Set a username for your profile.</Text>
          <Input
            placeholder='Username'
            value={usernameInput}
            onChange={(e) => setUsernameInput(e.target.value)}
            onPressEnter={onUsernameOk}
          />
        </Space>
      </Modal>
      <Modal
        title='Change Avatar'
        open={isAvatarModalOpen}
        onOk={onAvatarOk}
        onCancel={onAvatarCancel}
        okText='Save'
        footer={[
          <Button key='cancel' onClick={onAvatarCancel}>
            Cancel
          </Button>,
          <Button key='submit' type='primary' loading={avatarSaving} disabled={!avatarBase64 || !!avatarError} onClick={onAvatarOk}>
            Save
          </Button>,
        ]}
        maskClosable={true}
        closable={false}
        className={'settings-page-modal'}>
        <Space direction='vertical' style={{ width: '100%' }}>
          {avatarError && <Alert message={avatarError} type='error' showIcon />}
          <Text>Upload new avatar, make sure to keep it at least 500 x 500 px.</Text>
          <Dragger {...uploadProps}>
            <p className='ant-upload-drag-icon'>
              <InboxOutlined />
            </p>
            <p className='ant-upload-text' style={{ marginBottom: '-15px' }}>
              Click or drag file to this area to upload
            </p>
            <p className='ant-upload-hint'>Support for a single upload.</p>
          </Dragger>
        </Space>
      </Modal>
    </div>
  )
}
